import { t } from 'i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import useToast from 'src/hooks/useToast';
import {
  useDeleteTournamentMutation,
  useLazyGetMinimalTeamsQuery,
  useLazyGetOptionsQuery,
  useLazyGetTournamentScheduleQuery,
  useLazyGetTournamentsQuery,
  useLazyGetTournamentsWithoutPaginationQuery,
} from 'src/services/tournaments';
import { TournamentsStatus } from 'src/services/tournaments/getTournaments';
import {
  setMeta,
  setMinimalTeams,
  setMinimalTournaments,
  setOptions,
  setTournamentSchedule,
} from 'src/store/apps/tournament/TournamentSlice';
import { setTournaments } from 'src/store/apps/tournament/TournamentSlice';

const useTournament = () => {
  const { toastPromise } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { status, id } = useParams();

  const [getTournaments, getTournamentsRes] = useLazyGetTournamentsQuery();
  const [deleteTournamentReq] = useDeleteTournamentMutation();
  const [getOptions] = useLazyGetOptionsQuery();
  const [getTournamentSchedule, getTournamentScheduleRes] = useLazyGetTournamentScheduleQuery();
  const [getMinimalTeams, getMinimalTeamsRes] = useLazyGetMinimalTeamsQuery();
  const [getTournamentsWithoutPagination] = useLazyGetTournamentsWithoutPaginationQuery();

  const loadTournaments = (
    rowsPerPage: number,
    page: number,
    search?: string,
    signal?: AbortSignal,
  ) => {
    getTournaments({
      status: status === 'active' ? TournamentsStatus.ACTIVE : TournamentsStatus.DEACTIVATED,
      limit: rowsPerPage,
      page: page + 1,
      search: search || '',
      signal,
    }).then((res) => {
      const tournaments = res.data?.data;
      const meta = res.data?.meta;

      dispatch(setTournaments(tournaments));
      dispatch(setMeta(meta));
    });
  };

  const loadTournamentSchedule = () => {
    if (id) {
      getTournamentSchedule({ id: Number(id) }).then((res) => {
        const schedule = res.data;

        dispatch(setTournamentSchedule(schedule));
      });
    }
  };

  const loadOptions = () => {
    getOptions().then((res) => {
      const options = res.data;

      dispatch(setOptions(options));
    });
  };

  const loadMinimalTeams = () => {
    if (id) {
      getMinimalTeams(Number(id)).then((res) => {
        const teams = res.data?.data;

        const teamsArray = teams ? Object.values(teams).flat() : [];

        dispatch(setMinimalTeams(teamsArray));
      });
      getTournamentsWithoutPagination({
        status: status === 'active' ? TournamentsStatus.ACTIVE : TournamentsStatus.DEACTIVATED,
      }).then((res) => {
        const tournaments = res.data?.data;
        dispatch(setMinimalTournaments(tournaments));
      });
    }
  };

  const deleteTournament = (id: number, rowsPerPage?: number) => {
    toastPromise({
      request: () => {
        return deleteTournamentReq({ id });
      },
      onSuccess: (result: any) => {
        if ('error' in result) {
          throw result.error;
        }

        navigate(`/pages/tournaments/${status}`);
        loadTournaments(10, 0);

        return t('TournamentsScreen.tournamentDeletedSuccessfully');
      },
      onError: (err) => {
        if (err.data && err.data.errors) {
          const errorMessages = Object.values(err.data.errors).flat().join('. ');

          return errorMessages;
        }

        if (err?.data?.message) {
          return err.data.message;
        }

        return t('TournamentsScreen.errorOccurred');
      },
    });
  };

  return {
    loadTournaments,
    deleteTournament,
    isTournamentsLoading: getTournamentsRes.isLoading || getTournamentsRes.isFetching,
    loadOptions,
    isScheduleLoading: getTournamentScheduleRes.isLoading || getTournamentScheduleRes.isFetching,
    loadTournamentSchedule,
    loadMinimalTeams,
    isMinimalTeamsLoading: getMinimalTeamsRes.isLoading || getMinimalTeamsRes.isFetching,
  };
};

export default useTournament;
